import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Mail, Send, Check, AlertCircle } from "lucide-react";
import { subscribeNewsletter } from "@/lib/newsletter.functions";

export function NewsletterSection({ className }: { className?: string } = {}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const submit = useServerFn(subscribeNewsletter);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const result = await submit({ data: { email: email.trim() } });
      setStatus("success");
      setMessage(result.message);
      setEmail("");
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Napaka pri prijavi.");
    }
  };

  return (
    <section className={className ?? "bg-gradient-to-br from-accent/10 via-background to-primary/10 border-y border-accent/30 py-16 md:py-20"}>
      <div className="mx-auto max-w-3xl px-6 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-accent/30 bg-background mb-5">
          <Mail className="h-5 w-5 text-accent" />
        </div>
        <h2 className="font-serif text-3xl md:text-4xl">
          Prijavi se na <span>e-novice</span>
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Občasna sporočila o novih izdelkih, trgovinah in akcijah. Brez spama.
        </p>

        {status === "success" ? (
          <div className="mt-8 inline-flex items-center gap-2 border border-accent/40 bg-background px-6 py-4 text-sm text-accent">
            <Check className="h-4 w-4" />
            {message}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              required
              aria-label="E-pošta za novice"
              placeholder="vaš@email.si"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm tracking-wider uppercase hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              <Send className="h-3.5 w-3.5" />
              {status === "loading" ? "Pošiljam…" : "Prijavi se"}
            </button>
          </form>
        )}

        {status === "error" && (
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {message}
          </div>
        )}
      </div>
    </section>
  );
}
